"use strict";
// The PIE (P/Invoke for ECMAScript) bridge is responsible for routing JSON messages
// to/from JavaScript to the native hosts applications. Native hosts are typically
// written in C++, Java, or other languages.
//
// There is not a single approach for the communication between JavaScript and host
// and each JavaScript engine provides its own API for communication to/from the host.
// For this reason the PIE bridge is implemented in two parts: shared and host specific.
//
// The host specific abstracts the communication to/from JavaScript, while the shared
// portion of the bridge contains the script code that can be shared with all of the
// JavaScript hosts.
//
// The PIEBridge namespace contains the implementation of the shared bridge. The host
// bridge will latter provide the host bridge by calling the HostBridge.initialize
// function with an object that implements the HostSpecificBridge interface.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.IsVersonSupportedByHost = exports.isPIEHostPresent = exports.HostBridge = void 0;
// The PIE bridge provides support for the host to start work in JavaScript by using
// an event mechanism. Events will be started by the host and only processed if the
// script has registered to handle the event, the process of building the TypeScript
// files from a PIE schema will result in the event registration functions being created.
var RegisteredEventHandlers = /** @class */ (function () {
    function RegisteredEventHandlers() {
        this.nextHandlerId = 1;
        this.registeredHandlers = new Map();
    }
    // Registers a new event callback and returns the DisposeEventRegistration that
    // is used to unregister the event handler.
    RegisteredEventHandlers.prototype.registerHandler = function (callback) {
        var _this = this;
        // Get the id for this handler and then increment the nextHandlerId so the
        // it is ready for the next handler to be registered.
        var id = this.nextHandlerId;
        this.nextHandlerId = this.nextHandlerId + 1;
        // Add the handle to the set.
        this.registeredHandlers.set(id, callback);
        // capture the id in the closure and then return the function that
        // un-registers the handler using the id of the handler.
        return function () {
            _this.unregisterHandler(id);
        };
    };
    // When an event is received from the host the RaiseEvent function will be
    // called, this in turn invokes all of the registered handlers.
    RegisteredEventHandlers.prototype.RaiseEvent = function (args) {
        this.registeredHandlers.forEach(function (callback) {
            callback(args);
        });
    };
    // Returns true if there are any handlers registered
    RegisteredEventHandlers.prototype.HasHandlers = function () {
        return this.registeredHandlers.size !== 0;
    };
    // Un-registers the event handler
    RegisteredEventHandlers.prototype.unregisterHandler = function (id) {
        if (this.registeredHandlers.has(id)) {
            this.registeredHandlers.delete(id);
        }
    };
    return RegisteredEventHandlers;
}());
// The PIE shared bridge sends method invokes to the host and then receives
// the responses to those invoked as well as messages for any events raised
// by the host. The MessageProcessor class is responsible for sending messages
// to the host and calling the correct callbacks when a response or event is
// received from the host.
var MessageProcessor = /** @class */ (function () {
    function MessageProcessor() {
        this.nextMessageId = 1;
        this.pendingMessages = new Map();
        this.eventHandlers = new Map();
        this.abandonedCallHandlers = new RegisteredEventHandlers();
        this.namespaceVersionChangeHandlers = new RegisteredEventHandlers();
        this.hostNotificationHandlers = new RegisteredEventHandlers();
    }
    // When a registered namespace changes the host will send an idRequest that
    // indicates that the version has changed, it has a well know name and id
    MessageProcessor.prototype.isNamespaceVersionChange = function (envelope) {
        return envelope.isIdRequest
            && envelope.messageName === 'version-change'
            && envelope.messageId === -3;
    };
    // When the host specific bridge get a JSON message from the host it will call
    // the onMessageFromHost method to handle the message.
    MessageProcessor.prototype.onMessageFromHost = function (envelope) {
        // The host/script pipe may be used for non PIE messages, check to make sure
        // the JSON object is intended for for the PIE bridge. Borrowing the Content-Type
        // header concept from http we confirm that the contentType exists and matches
        // the expected 'app/pie' content.
        if (envelope.contentType === 'app/pie') {
            this.raiseMessageInfoIfNeeded(envelope, /*isToHost*/ false);
            if (envelope.isEvent) {
                // The envelope is wrapping an event, if there are any handlers
                // registered for the event we should then raise the event.
                var name_1 = envelope.messageName;
                if (this.eventHandlers.has(name_1)) {
                    this.eventHandlers.get(name_1).RaiseEvent(envelope.args);
                }
            }
            else {
                // The envelope is wrapping a response to a message sent by this bridge, use
                // the message id to find the associated pending promise.
                var id = envelope.messageId;
                if (this.pendingMessages.has(id)) {
                    var messagePromise = this.pendingMessages.get(id);
                    this.pendingMessages.delete(id);
                    if (envelope.abandoned) {
                        // the message has been abandoned, notify any listeners
                        // that the message was abandoned
                        this.abandonedCallHandlers.RaiseEvent(envelope.messageName);
                    }
                    else if (envelope.wasSuccessful) {
                        // message was successful, complete the promise
                        messagePromise.resolve(messagePromise.postProcessing
                            ? messagePromise.postProcessing(envelope.message)
                            : envelope.message);
                    }
                    else {
                        // message failed reject the promise
                        messagePromise.reject(new Error(envelope.messageName + ':' + envelope.errorMessage));
                    }
                }
                else if (this.isNamespaceVersionChange(envelope)) {
                    // the registered version of a namespace has changed
                    this.namespaceVersionChangeHandlers.RaiseEvent(envelope.message);
                }
            }
        }
    };
    // In order to invoke a message on the host we need to build an envelope that
    // will provide the host with enough information to correctly handle the message.
    MessageProcessor.prototype.prepareMessageEnvelope = function (name, isIdRequest, messageObject, resolveCallback, rejectCallback, postProcessing) {
        // get the id that should be used for this message
        var id = this.nextMessageId;
        // While it is highly unlikely that we will send more messages than the maximum
        // safe integer, in the event that we do we want to wrap around to 1 so that we
        // will continue to get incrementing id values.
        this.nextMessageId = (this.nextMessageId === Number.MAX_SAFE_INTEGER) ? 1 : this.nextMessageId + 1;
        // Add the resolve and reject callbacks to the pending messages
        var messagePromise = { resolve: resolveCallback, reject: rejectCallback, postProcessing: postProcessing };
        this.pendingMessages.set(id, messagePromise);
        if (isIdRequest) {
            return { contentType: 'app/pie', messageName: name, isIdRequest: true, messageId: id, message: messageObject };
        }
        return { contentType: 'app/pie', messageName: name, messageId: id, message: messageObject };
    };
    // Implementation wise an id request is simply a invoking a method that will be handled
    // the by host implementation of the PIE shared bridge. Rather than relying on just the name
    // of the method the envelope has an isIdRequest member which will tell the host that this
    // message should be processed by the PIE shared bridge.
    MessageProcessor.prototype.invokeIdRequest = function (bridge, name, idData, resolveCallback, rejectCallback) {
        var envelope = this.prepareMessageEnvelope(name, true, idData, resolveCallback, rejectCallback);
        this.raiseMessageInfoIfNeeded(envelope, /*isToHost*/ true, /*isAsync*/ true);
        bridge.sendMessageToHost(envelope);
    };
    MessageProcessor.throwPieSyncNotSupported = function () {
        throw new Error("Synchronous bridge not supported");
    };
    // Sends a request to the host to invoke the named method
    MessageProcessor.prototype.invokeOnHostSync = function (bridge, name, messageObject, resolveCallback, rejectCallback, postProcessing) {
        var envelope = this.prepareMessageEnvelope(name, false, messageObject, resolveCallback, rejectCallback, postProcessing);
        if (bridge.sendMessageToHostSync) {
            this.raiseMessageInfoIfNeeded(envelope, /*isToHost*/ true, /*isAsync*/ false);
            bridge.sendMessageToHostSync(envelope);
        }
        else {
            MessageProcessor.throwPieSyncNotSupported();
        }
    };
    // Sends a request to the host to invoke the named method
    MessageProcessor.prototype.invokeOnHost = function (bridge, name, messageObject, resolveCallback, rejectCallback, postProcessing) {
        var envelope = this.prepareMessageEnvelope(name, false, messageObject, resolveCallback, rejectCallback, postProcessing);
        this.raiseMessageInfoIfNeeded(envelope, /*isToHost*/ true, /*isAsync*/ true);
        bridge.sendMessageToHost(envelope);
    };
    // Registers the specified callback to receive the named event. The returned
    // DisposeEventRegistration can be called to unregister the event listener.
    MessageProcessor.prototype.registerForEvent = function (eventName, callback) {
        if (!this.eventHandlers.has(eventName)) {
            this.eventHandlers.set(eventName, new RegisteredEventHandlers());
        }
        return this.eventHandlers.get(eventName).registerHandler(callback);
    };
    // Registers for notification of abandoned calls
    MessageProcessor.prototype.registerForAbandonedCalls = function (callback) {
        return this.abandonedCallHandlers.registerHandler(callback);
    };
    // Registers for notification when a namespace version is changed
    MessageProcessor.prototype.registerForNamespaceVersionChange = function (callback) {
        return this.namespaceVersionChangeHandlers.registerHandler(callback);
    };
    // Registers for notification when a message is sent to/from the Host
    MessageProcessor.prototype.registerForHostMessageNotification = function (callback) {
        return this.hostNotificationHandlers.registerHandler(callback);
    };
    // Raises the message info event if there are registered handlers
    MessageProcessor.prototype.raiseMessageInfoIfNeeded = function (envelope, isToHost, isAsync) {
        if (this.hostNotificationHandlers.HasHandlers()) {
            var args = {
                isToHost: isToHost,
                isAsync: isAsync,
                messageName: envelope.messageName,
                isIdRequest: envelope.isIdRequest,
                messageId: envelope.messageId,
                isEvent: envelope.isEvent,
                wasSuccessful: envelope.wasSuccessful,
                abandoned: envelope.abandoned
            };
            this.hostNotificationHandlers.RaiseEvent(args);
        }
    };
    return MessageProcessor;
}());
// Responsible for the implementation of the shared host bridge.
var HostBridgeImpl = /** @class */ (function () {
    // Creates a new instance of the HostBridgeImpl object that will be hooked up
    // to the specified host bridge.
    function HostBridgeImpl(bridge) {
        this.bridge = bridge;
        this.processor = new MessageProcessor();
        this.instanceId = null;
        this.namespaceVersions = new Map();
        // Register for version registration changes 
        var versionChange = function (namespaceName) {
            if (this.namespaceVersions.has(namespaceName)) {
                this.namespaceVersions.delete(namespaceName);
            }
        }.bind(this);
        this.processor.registerForNamespaceVersionChange(versionChange);
        // Add the message handler to the bridge
        var messageHandler = function (envelope) {
            this.processor.onMessageFromHost(envelope);
        }.bind(this);
        this.bridge.addHostMessageHandler(messageHandler);
    }
    // Return true if the specified bridge is the bridge used by this instance
    HostBridgeImpl.prototype.isUsingBridge = function (bridge) {
        return this.bridge === bridge;
    };
    // Invokes the specified function on the host
    HostBridgeImpl.prototype.invokeHost = function (hostFunctionName, message, postProcessing) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            _this.processor.invokeOnHost(_this.bridge, hostFunctionName, message, resolve, reject, postProcessing);
        });
    };
    // Syncrhonously invokes the specified function on the host
    HostBridgeImpl.prototype.invokeHostSync = function (hostFunctionName, message, postProcessing) {
        var result;
        var resolve = function (response) {
            result = response;
        };
        var reject = function (errorMessage) {
            throw errorMessage;
        };
        this.processor.invokeOnHostSync(this.bridge, hostFunctionName, message, resolve, reject, postProcessing);
        return result;
    };
    // Requests the specified id from the host shared bridge
    HostBridgeImpl.prototype.invokeIdRequestOnHost = function (idName, idData) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            _this.processor.invokeIdRequest(_this.bridge, idName, idData, resolve, reject);
        });
    };
    // Registers for the specified name event
    HostBridgeImpl.prototype.registerForEvent = function (eventName, callback) {
        return this.processor.registerForEvent(eventName, callback);
    };
    // Registers for notification of abandoned calls
    HostBridgeImpl.prototype.registerForAbandonedCalls = function (callback) {
        return this.processor.registerForAbandonedCalls(callback);
    };
    // Registers for notification when a message is sent to/from the Host
    HostBridgeImpl.prototype.registerForHostMessageNotification = function (callback) {
        return this.processor.registerForHostMessageNotification(callback);
    };
    // Returns the instance id of the PIE bridge
    HostBridgeImpl.prototype.getInstanceId = function () {
        var _this = this;
        return new Promise(function (resolve, reject) {
            if (_this.instanceId) {
                // the value is cached return the cached value
                resolve(_this.instanceId);
            }
            else {
                // call the native host to get the value
                _this.invokeIdRequestOnHost('instance', 'id').then(function (id) {
                    _this.instanceId = id;
                    resolve(_this.instanceId);
                }).catch(function (errorMessage) {
                    reject(errorMessage);
                });
            }
        });
    };
    // gets the namespace version either from the cache or by requesting from the host
    HostBridgeImpl.prototype.getNamespaceVersion = function (namespaceName) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            if (_this.namespaceVersions.has(namespaceName)) {
                resolve(_this.namespaceVersions.get(namespaceName));
                return;
            }
            _this.invokeIdRequestOnHost('version', namespaceName).then(function (value) {
                var version = parseFloat(value);
                if (isNaN(version)) {
                    reject("Non numeric version returned");
                }
                else {
                    _this.namespaceVersions.set(namespaceName, version);
                    resolve(version);
                }
            }).catch(function (errorMessage) {
                reject(errorMessage);
            });
        });
    };
    return HostBridgeImpl;
}());
// The shared PIE bridge is implemented using a singleton pattern so that we can avoid the
// complexities of determining which PIE shared bridge needs to handle which message. The
// HostBridge class is responsible for abstracting the creation and
var HostBridge = /** @class */ (function () {
    function HostBridge() {
    }
    // Returns true if the PIE bridge needs to be initialized for the specified host bridge.
    // Initialization is needed when the bridge has not yet been initialized or is initialized
    // using a different host specific bridge
    HostBridge.isInitializationNeeded = function (bridge) {
        return !(HostBridge._implInstance) ||
            (!HostBridge._implInstance.isUsingBridge(bridge));
    };
    // Initializes the shared bridge using the supplied host specific bridge, if the shared
    // bridge is already initialized for the specified bridge nothing is done.
    HostBridge.initialize = function (bridge) {
        if (HostBridge.isInitializationNeeded(bridge)) {
            HostBridge._implInstance = new HostBridgeImpl(bridge);
        }
    };
    // When unit testing the PIE bridge we need the ability to reset it to an
    // uninitialized state, this method does that. It is recommended that this
    // not be called in production as it will cause registered event handlers and
    // pending messages to be lost.
    HostBridge.resetBridgeForUnitTests = function () {
        HostBridge._implInstance = null;
    };
    // Creates a promise object that will reject the promise with the reason that
    // the bridge is not initialized.
    HostBridge.failBridgeNotInitialized = function () {
        return new Promise(function (resolve, reject) {
            reject(new Error("HostBridge not initialized"));
            return;
        });
    };
    // Invokes the specified function on host.
    HostBridge.invokeHost = function (hostFunctionName, message, postProcessing) {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                if (HostBridge._implInstance) {
                    return [2 /*return*/, HostBridge._implInstance.invokeHost(hostFunctionName, message, postProcessing)];
                }
                return [2 /*return*/, HostBridge.failBridgeNotInitialized()];
            });
        });
    };
    // Invokes the specified function on the host synchronously, this method will throw
    // if the host does not support synchronous methods or the method is not supported
    HostBridge.invokeHostSync = function (hostFunctionName, message, postProcessing) {
        if (HostBridge._implInstance) {
            return HostBridge._implInstance.invokeHostSync(hostFunctionName, message, postProcessing);
        }
        throw new Error("HostBridge not initialized");
    };
    // Get the instance id of the bridge, this instance id
    HostBridge.getInstanceId = function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                if (HostBridge._implInstance) {
                    return [2 /*return*/, HostBridge._implInstance.getInstanceId()];
                }
                return [2 /*return*/, HostBridge.failBridgeNotInitialized()];
            });
        });
    };
    // Get the version of the specified namespace
    HostBridge.getNamespaceVersion = function (namespaceName) {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                if (HostBridge._implInstance) {
                    return [2 /*return*/, HostBridge._implInstance.getNamespaceVersion(namespaceName)];
                }
                return [2 /*return*/, HostBridge.failBridgeNotInitialized()];
            });
        });
    };
    // Registers an event handler callback for the specified event
    HostBridge.registerForEvent = function (eventName, callback) {
        if (HostBridge._implInstance) {
            return HostBridge._implInstance.registerForEvent(eventName, callback);
        }
        return null;
    };
    // Registers for notification of abandoned calls
    HostBridge.registerForAbandonedCalls = function (callback) {
        if (HostBridge._implInstance) {
            return HostBridge._implInstance.registerForAbandonedCalls(callback);
        }
        return null;
    };
    // To enable script side telemetry support is provided for registering a callback that will
    // be notified when a message is sent to or received from the host. Rather than provide the
    // whole message the callback provides meta information about the message being sent.
    HostBridge.registerForHostMessageNotification = function (callback) {
        if (HostBridge._implInstance) {
            return HostBridge._implInstance.registerForHostMessageNotification(callback);
        }
        return null;
    };
    HostBridge.throwPieSyncNotSupported = function () {
        MessageProcessor.throwPieSyncNotSupported();
    };
    // The single instance of the PIE bridge
    HostBridge._implInstance = null;
    return HostBridge;
}());
exports.HostBridge = HostBridge;
// Returns true if the PIE Host bridge is present and can be communicated with
function isPIEHostPresent() {
    return __awaiter(this, void 0, void 0, function () {
        return __generator(this, function (_a) {
            return [2 /*return*/, new Promise(function (resolve) {
                    HostBridge.getInstanceId().then(function (instanceId) {
                        resolve(instanceId ? true : false);
                    }).catch(function () {
                        resolve(false);
                    });
                })];
        });
    });
}
exports.isPIEHostPresent = isPIEHostPresent;
// Determines if the PIE host bridge is present and if the version is supported
// by the host. If the 'end' value is specified a range of versions will be checked
//      namespaceName - the namespace for which the version is to be checked
//      start - the minimum needed version
//      end - the first version which is considered unsupported
function IsVersonSupportedByHost(namespaceName, min, end) {
    return __awaiter(this, void 0, void 0, function () {
        var isHostPresent, hostVersion;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, isPIEHostPresent()];
                case 1:
                    isHostPresent = _a.sent();
                    if (!isHostPresent) return [3 /*break*/, 3];
                    return [4 /*yield*/, HostBridge.getNamespaceVersion(namespaceName)];
                case 2:
                    hostVersion = _a.sent();
                    if (hostVersion >= min) {
                        if (!end || (hostVersion < end)) {
                            return [2 /*return*/, true];
                        }
                    }
                    _a.label = 3;
                case 3: return [2 /*return*/, false];
            }
        });
    });
}
exports.IsVersonSupportedByHost = IsVersonSupportedByHost;
//# sourceMappingURL=PieBridge.js.map